import { VehicleAlert, VehicleAlertStatus } from '@/services/api/monitoring';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Format an ISO/UTC timestamp in the client's local timezone.
 * `toLocaleString` with no explicit `timeZone` renders in the runtime
 * (browser) timezone, i.e. the client's timezone.
 */
export const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Humanized minutes, e.g. 125 -> "2h 5m", 1620 -> "1d 3h". */
export const formatDuration = (minutes?: number | null): string => {
  if (minutes === undefined || minutes === null || Number.isNaN(minutes)) {
    return '-';
  }
  if (minutes < 1) return '< 1m';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = Math.round(minutes % 60);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && !days) parts.push(`${mins}m`);
  return parts.length ? parts.join(' ') : '< 1m';
};

/** Render a context value; ISO timestamps are normalized to client tz. */
export const formatContextValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return formatDateTime(value);
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

/**
 * Key-aware context formatting: minute-valued fields (e.g. ageMinutes,
 * thresholdMinutes) are shown as a humanized duration instead of a raw
 * number; everything else falls back to formatContextValue.
 */
export const formatContextEntry = (key: string, value: unknown): string => {
  if (typeof value === 'number' && /minutes$/i.test(key)) {
    return formatDuration(value);
  }
  return formatContextValue(value);
};

export const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Stale aging = how long the vehicle's telemetry has been stale, measured
 * from the last telemetry timestamp (context.lastTelemetryAt) — distinct
 * from `durationMinutes`, which counts from when the alert fired.
 *
 * For OPEN alerts this is live (now - lastTelemetryAt); for RESOLVED alerts
 * it is measured up to resolvedAt. Falls back to the snapshot the backend
 * recorded at fire time (context.ageMinutes) when lastTelemetryAt is absent.
 */
export const getStaleAgingMinutes = (alert: VehicleAlert): number | null => {
  const ctx = (alert.context ?? {}) as Record<string, unknown>;

  const lastTelemetryAt = ctx.lastTelemetryAt;
  if (typeof lastTelemetryAt === 'string') {
    const last = new Date(lastTelemetryAt).getTime();
    if (!Number.isNaN(last)) {
      const end =
        alert.status === VehicleAlertStatus.RESOLVED && alert.resolvedAt
          ? new Date(alert.resolvedAt).getTime()
          : Date.now();
      return Math.max(0, Math.round((end - last) / 60000));
    }
  }

  const age = ctx.ageMinutes;
  if (typeof age === 'number' && !Number.isNaN(age)) return age;

  return null;
};

export const getLastTelemetryAt = (alert: VehicleAlert): string | null => {
  const ctx = (alert.context ?? {}) as Record<string, unknown>;
  return typeof ctx.lastTelemetryAt === 'string' ? ctx.lastTelemetryAt : null;
};

/** The staleness threshold (minutes) that triggered the alert, if recorded. */
export const getThresholdMinutes = (alert: VehicleAlert): number | null => {
  const ctx = (alert.context ?? {}) as Record<string, unknown>;
  return typeof ctx.thresholdMinutes === 'number' ? ctx.thresholdMinutes : null;
};
